import { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Image as ImageIcon, Upload, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const TenantBannerCard = () => {
  const fileRef = useRef(null);
  const [banner, setBanner] = useState(null);
  const [slug, setSlug] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/branding/banner`, { withCredentials: true });
      setBanner(data.login_banner_url);
      setSlug(data.slug);
    } catch {
      setBanner(null);
    }
  };

  useEffect(() => { load(); }, []);

  const upload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(`${BACKEND_URL}/api/branding/banner`, form, { withCredentials: true });
      toast.success('Banner atualizado');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar o banner');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/branding/banner`, { withCredentials: true });
      toast.success('Banner removido');
      setBanner(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover');
    } finally {
      setBusy(false);
    }
  };

  const loginUrl = slug ? `${window.location.origin}/login-local?org=${slug}` : null;

  return (
    <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800" data-testid="tenant-banner-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <ImageIcon className="w-6 h-6 text-[#10B981]" />
          <div>
            <CardTitle className="dark:text-white">Banner da Organização</CardTitle>
            <CardDescription className="mt-1 dark:text-slate-400">
              PNG, JPG ou WebP · recomendado 1820x400 px · máx. 3 MB
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {banner ? (
          <img
            src={`${BACKEND_URL}${banner}?t=${Date.now()}`}
            alt="Banner"
            data-testid="tenant-banner-preview"
            className="w-full rounded-lg border border-slate-200 object-cover"
            style={{ aspectRatio: '1820 / 400' }}
          />
        ) : (
          <div
            className="w-full rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 flex items-center justify-center text-slate-400 text-sm"
            style={{ aspectRatio: '1820 / 400' }}
          >
            Sem banner definido
          </div>
        )}

        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={upload}
          className="hidden"
          data-testid="banner-file-input"
        />

        <div className="flex flex-wrap gap-3">
          <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="upload-banner-button">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {banner ? 'Substituir' : 'Carregar banner'}
          </Button>
          {banner && (
            <Button variant="outline" onClick={remove} disabled={busy} data-testid="remove-banner-button">
              <Trash2 className="w-4 h-4 mr-2" /> Remover
            </Button>
          )}
        </div>

        {loginUrl && (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Página de login personalizada:{' '}
            <a href={loginUrl} target="_blank" rel="noreferrer" className="text-[#10B981] hover:underline" data-testid="tenant-login-url">
              {loginUrl}
            </a>
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default TenantBannerCard;
